import { TrackedElement } from '@chewy/use-analytics';
import cx from 'classnames';

import { PlanOption, PriceType } from '../../apollo/__generated__/global-types';
import { getCheapestPlan } from '../../utils/plans';
import { ANALYTICS_TAG, useAnalyticsContext } from '../Analytics/AnalyticsProvider';
import { FormAnchorLink } from '../GoToFormButton/FormAnchorLink';
import { FORM_LAUNCH_STORAGE_KEY } from '../MealPlanForm/utils';
import { FreshPlanRecommendationsQuery } from '../MealPlanFormSteps/__generated__/chewy-api.plan-recommendations';
import { usePetProfileContext } from '../PetProfileProvider/PetProfileProvider';

import styles from './styles.module.scss';
import { getPreChosenPetData, PlaFlavorType, PlaFrequency } from './utils';

import flavorBeef from '@/assets/get-real-beef-and-sweet-potato-recipe-packaging.png';
import flavorChicken from '@/assets/get-real-chicken-and-brussels-sprouts-recipe-packaging.png';
import flavorTurkey from '@/assets/get-real-turkey-and-cranberry-recipe-packaging.png';

const getDisplayDataForPlaType = (type: PlaFlavorType) => {
  const images = {
    [PlaFlavorType.Chicken]: flavorChicken.src,
    [PlaFlavorType.Beef]: flavorBeef.src,
    [PlaFlavorType.Turkey]: flavorTurkey.src,
  };
  const flavors = {
    [PlaFlavorType.Chicken]: 'Chicken Flavor',
    [PlaFlavorType.Beef]: 'Beef Flavor',
    [PlaFlavorType.Turkey]: 'Turkey Flavor',
  };
  if (type !== PlaFlavorType.VarietyPack) {
    return {
      images: [images[type as keyof typeof images]],
      flavors: [flavors[type as keyof typeof flavors]],
      heading: type + ' Recipe',
      subHeading: '(Other flavors available)',
    };
  }
  // for variety pack, we want to return all three images
  return {
    images: [flavorChicken.src, flavorBeef.src, flavorTurkey.src],
    flavors: Object.values(flavors),
    heading: 'Variety Pack',
    subHeading: '(All 3 flavors included)',
  };
};

export const PlaPreChosenPlan = ({
  plaPlan,
  type,
  frequency = 'monthly',
}: {
  plaPlan: FreshPlanRecommendationsQuery | undefined;
  type: PlaFlavorType;
  frequency?: PlaFrequency;
}) => {
  const { track } = useAnalyticsContext();
  const { updateSelectedPet, clearSelectedPet, clearStoredPet } = usePetProfileContext();

  const handleChoosePlan = () => {
    // Store the current URL path to return to on form exit
    // localStorage is used because there's no shared providers amongst the different pages
    localStorage.setItem(FORM_LAUNCH_STORAGE_KEY, window.location.pathname);

    track(ANALYTICS_TAG.NAME_BUTTON_CLICKED, {
      eventCategory: ANALYTICS_TAG.CATEGORY_PLA,
      eventAction: ANALYTICS_TAG.EVENT_CLICK,
      eventLabel: 'get-real-choose-prechosen-plan',
    });

    clearSelectedPet();
    clearStoredPet();
    updateSelectedPet(getPreChosenPetData());
  };

  const handleBuildYourOwnPlan = () => {
    // Store the current URL path to return to on form exit
    // localStorage is used because there's no shared providers amongst the different pages
    localStorage.setItem(FORM_LAUNCH_STORAGE_KEY, window.location.pathname);

    track(ANALYTICS_TAG.NAME_BUTTON_CLICKED, {
      eventCategory: ANALYTICS_TAG.CATEGORY_PLA,
      eventAction: ANALYTICS_TAG.EVENT_CLICK,
      eventLabel: 'get-real-build-own-plan',
    });
  };

  if (!plaPlan) return null;

  const recommendations = plaPlan?.planRecommendationsV2?.results;
  const plan = getCheapestPlan(recommendations, PlanOption.FullMeal);

  if (!plan) return null;

  const perUnitPriceStrikethrough =
    plan.pricing?.find((price) => price?.type === PriceType.Strikethrough)?.perUnitPrice?.amount ??
    0;
  const perUnitPriceOffer =
    plan.pricing?.find((price) => price?.type === PriceType.Offer)?.perUnitPrice?.amount ?? 0;
  const size = plan?.planItems[0]?.item?.size ?? undefined;
  const sizeNumber = Number(size?.split('-')[0]);
  const recommendedOzPerDay = Number(plan.feedingRecommendations?.value) || 0;

  const perDayPriceStrikethrough =
    recommendedOzPerDay > 0 ? (recommendedOzPerDay / sizeNumber) * perUnitPriceStrikethrough : 0;
  const perWeekPriceStrikethrough = Math.round(perDayPriceStrikethrough * 7 * 100) / 100;
  const perDayPriceOffer =
    recommendedOzPerDay > 0 ? (recommendedOzPerDay / sizeNumber) * perUnitPriceOffer : 0;
  const perWeekPriceOffer = Math.round(perDayPriceOffer * 7 * 100) / 100;
  const perMonthPriceOffer = Math.round(perDayPriceOffer * 30 * 100) / 100;
  const perMonthPriceStrikethrough = Math.round(perDayPriceStrikethrough * 30 * 100) / 100;

  // we always want to show the full price. OR operator used here to avoid zero values.
  const pricePerWeek = perWeekPriceStrikethrough || perWeekPriceOffer;
  const pricePerDay = perDayPriceStrikethrough || perDayPriceOffer;
  const pricePerMonth = perMonthPriceStrikethrough || perMonthPriceOffer;

  // Determine display price and total based on frequency
  let displayPrice: number;
  let displayTimePeriod: string;
  let displayTotal: number | null = null;

  if (frequency === 'daily') {
    displayPrice = pricePerDay;
    displayTimePeriod = 'day';
  } else if (frequency === 'monthly') {
    displayPrice = pricePerWeek;
    displayTimePeriod = 'week';
    displayTotal = pricePerMonth;
  } else {
    // weekly (default)
    displayPrice = pricePerWeek;
    displayTimePeriod = 'week';
  }

  const displayData = getDisplayDataForPlaType(type);

  return (
    <TrackedElement
      listeners={{
        onVisibleOnce: {
          event: ANALYTICS_TAG.NAME_MODULE_VIEWED,
        },
      }}
      properties={{
        eventCategory: ANALYTICS_TAG.CATEGORY_PLA,
        eventAction: ANALYTICS_TAG.EVENT_IMPRESSION,
        eventLabel: 'get-real-pla-module',
      }}
    >
      <div className={styles.root}>
        <h3 className={styles.mealType}>Full Meal</h3>
        <h2 className={styles.heading}>{displayData.heading}</h2>
        <div className={styles.flavorInfo}>{displayData.subHeading}</div>
        <div
          className={cx(styles.images, {
            [styles.burstBackground]: displayData.images.length === 1,
          })}
        >
          {displayData.images?.map((image, index) => (
            <div
              className={cx({
                [styles.singularImage]: displayData.images.length === 1,
                [styles.multiImage]: displayData.images.length > 1,
              })}
              key={displayData.flavors[index]}
            >
              <img src={image} alt={displayData.flavors[index]} />
            </div>
          ))}
        </div>
        <div className={styles.pricing}>
          <div className={styles.startingAt}>Starting at</div>
          <span className={styles.perWeekPrice}>
            ${displayPrice.toFixed(2)}/{displayTimePeriod}
          </span>
          {displayTotal !== null && (
            <>
              {' '}
              | <span className={styles.totalPrice}>${displayTotal.toFixed(2)} total</span>
            </>
          )}
        </div>
        <div className={styles.pricingNote}>(Pricing may vary due to plan personalization)</div>
        <div className={styles.renewalInfo}>
          {plan.subscriptionFrequency?.value &&
            `Renews every ${plan.subscriptionFrequency?.value} weeks. Cancel any time.`}
          <br /> New customers get 50% off at checkout.
        </div>
        <div className={styles.buttonWrapper}>
          <FormAnchorLink
            onClick={handleChoosePlan}
            text="Choose this plan"
            className={styles.button}
          />

          <FormAnchorLink
            onClick={handleBuildYourOwnPlan}
            text="Build your own"
            className={styles.secondaryButton}
            emphasis="secondary"
          />
        </div>
        <div className={styles.dogNote}>*Adult dogs are 1 year +</div>
      </div>
    </TrackedElement>
  );
};
